import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import type { StoreApi } from 'zustand'
import { createLayoutStore } from './store'
import type { LayoutStoreState, LayoutStoreConfig } from './store'
import { LayoutStoreContext } from './hooks'
import { registerLayoutStore } from './registry'

type LayoutStore = StoreApi<LayoutStoreState>

interface LayoutStoreProviderProps {
  config: LayoutStoreConfig
  children: ReactNode
}

export function LayoutStoreProvider({ config, children }: LayoutStoreProviderProps) {
  const ref = useRef<{
    store: LayoutStore
    destroy: () => void
  } | null>(null)

  if (!ref.current) {
    ref.current = createLayoutStore(config)
  }

  // Register the store globally so cross-tree imperative callers (notably
  // `useExpressionLibrary`'s propagation cascade) can mutate it without
  // walking React context. The dispose fires before the store is destroyed
  // below so we never hand out a half-torn-down handle.
  useEffect(() => {
    if (!ref.current) return
    return registerLayoutStore(ref.current.store)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      ref.current?.destroy()
    }
  }, [])

  return <LayoutStoreContext value={ref.current.store}>{children}</LayoutStoreContext>
}
